import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';

import {
  createFeatureAssociation,
  deleteFeatureAssociation,
  editFeatureAssociation,
  loadCustomerFeatureAssociations,
  loadFeatures,
  loadSiteFeatureAssociations,
  loadSiteGroupFeatureAssociations,
} from '@sophocles/features/featureFlags/api';
import { FeatureAssociationSchema, FeatureSchema } from '@sophocles/features/featureFlags/schema';

const featureEntityAdapter = createEntityAdapter({
  selectId: feature => feature.key,
});
const featureAssociationAdapter = createEntityAdapter();

const slice = createSlice({
  name: 'features',
  initialState: {
    features: featureEntityAdapter.getInitialState({}),
    featureAssociations: featureAssociationAdapter.getInitialState({}),
    view: {
      search: null,
      page: 0,
      order: 'asc',
      orderBy: 'id',
    },
  },
  reducers: {
    setPage(state, action) {
      state.view.page = action.payload;
    },
    setSearch(state, action) {
      state.view.search = action.payload;
    },
    setOrder(state, action) {
      state.view.order = action.payload;
    },
    setOrderBy(state, action) {
      state.view.orderBy = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadFeatures.fulfilled, (state, action) => {
        const { entities } = normalize(action.payload.results, [FeatureSchema]);
        featureEntityAdapter.upsertMany(state.features, entities.features ?? {});
      })
      .addCase(loadCustomerFeatureAssociations.fulfilled, (state, action) => {
        const { entities } = normalize(action.payload.results, [FeatureAssociationSchema]);
        featureAssociationAdapter.upsertMany(state.featureAssociations, entities.featureAssociations ?? {});
        featureEntityAdapter.upsertMany(state.features, entities.features ?? {});
      })
      .addCase(loadSiteGroupFeatureAssociations.fulfilled, (state, action) => {
        const { entities } = normalize(action.payload.results, [FeatureAssociationSchema]);
        featureAssociationAdapter.upsertMany(state.featureAssociations, entities.featureAssociations ?? {});
        featureEntityAdapter.upsertMany(state.features, entities.features ?? {});
      })
      .addCase(loadSiteFeatureAssociations.fulfilled, (state, action) => {
        const { entities } = normalize(action.payload.results, [FeatureAssociationSchema]);
        featureAssociationAdapter.upsertMany(state.featureAssociations, entities.featureAssociations ?? {});
        featureEntityAdapter.upsertMany(state.features, entities.features ?? {});
      })
      .addCase(createFeatureAssociation.fulfilled, (state, action) => {
        const featureAssociation = action.payload;
        const feature = featureAssociation.feature;
        featureAssociation.feature = feature.key;
        featureAssociationAdapter.upsertOne(state.featureAssociations, featureAssociation);
        featureEntityAdapter.upsertOne(state.features, feature);
      })
      .addCase(editFeatureAssociation.fulfilled, (state, action) => {
        const featureAssociation = action.payload;
        const feature = featureAssociation.feature;
        featureAssociation.feature = feature.key;
        featureAssociationAdapter.upsertOne(state.featureAssociations, featureAssociation);
        featureEntityAdapter.upsertOne(state.features, feature);
      })
      .addCase(deleteFeatureAssociation.fulfilled, (state, action) => {
        const { id } = action.meta.arg;
        featureAssociationAdapter.removeOne(state.featureAssociations, id);
      });
  },
});
// Base selectors
export const selectFeatureEntities = state => state.features.features.entities;
export const selectFeatureAssociationEntities = state => state.features.featureAssociations.entities;

// Select all features
export const selectAllFeatures = createSelector(selectFeatureEntities, features => Object.values(features));

// Select all denormalized feature associations
export const selectDenormalizedFeatureAssociations = createSelector(
  [selectFeatureEntities, selectFeatureAssociationEntities],
  (features, featureAssociations) =>
    Object.values(featureAssociations).map(association => ({
      ...association,
      feature: features[association.feature], // Link the associated feature
    })),
);

// Select autocomplete options for features
export const selectFeatureAutocompleteOptions = createSelector(selectAllFeatures, features =>
  features.map(feature => ({
    label: feature.name,
    value: feature.key,
    is_public: feature.is_public,
  })),
);

export const selectFeaturesByEntityType = entityType =>
  createSelector(selectAllFeatures, features =>
    features.filter(feature => feature.supported_entity_types.includes(entityType)),
  );

// Sorted version of selectFeaturesByEntityType keyed by a specific
// entity. Enabled-on-this-entity features come first; within each
// group, alphabetical by display_name. Used by both the V2 settings
// Features section and the EditSiteDialog Features tab so the two
// surfaces always present features in the same order.
export const selectSortedFeaturesForEntity = (entityType, entityId) =>
  createSelector(
    [selectFeaturesByEntityType(entityType), selectDenormalizedFeatureAssociations],
    (features, associations) => {
      const enabledKeys = new Set(
        associations
          .filter(a => a.entity_type === entityType && a.entity_id == entityId && a.is_effectively_enabled)
          .map(a => a.feature?.key)
          .filter(Boolean),
      );
      return [...features].sort((a, b) => {
        const aOn = enabledKeys.has(a.key) ? 0 : 1;
        const bOn = enabledKeys.has(b.key) ? 0 : 1;
        if (aOn !== bOn) return aOn - bOn;
        return (a.display_name || a.key).localeCompare(b.display_name || b.key);
      });
    },
  );

// Select denormalized feature associations by entity type
const selectFeatureAssociationsByEntityType = entityType =>
  createSelector(selectDenormalizedFeatureAssociations, featureAssociations =>
    featureAssociations.filter(association => association.entity_type === entityType),
  );

// Specific selectors for each entity type
export const selectCustomerFeatureAssociations = selectFeatureAssociationsByEntityType('customer');
export const selectSiteGroupFeatureAssociations = selectFeatureAssociationsByEntityType('sitegroup');
export const selectSiteFeatureAssociations = selectFeatureAssociationsByEntityType('site');

// Select feature associations by entity ID
export const selectFeatureAssociationsByEntityId = (entityTypeSelector, entityId) =>
  createSelector(entityTypeSelector, featureAssociations =>
    featureAssociations.filter(association => association.entity_id == entityId),
  );

export const selectFeatureAssociationByKeyAndId = (entityTypeSelector, entityId, featureKey) =>
  createSelector(entityTypeSelector, featureAssociations =>
    featureAssociations.find(
      association => association.feature?.key === featureKey && association.entity_id == entityId,
    ),
  );

// Specific selectors for entity IDs
export const selectFeatureAssociationsByCustomerId = customerId =>
  selectFeatureAssociationsByEntityId(selectCustomerFeatureAssociations, customerId);
export const selectFeatureAssociationsBySiteGroupId = siteGroupId =>
  selectFeatureAssociationsByEntityId(selectSiteGroupFeatureAssociations, siteGroupId);
export const selectFeatureAssociationsBySiteId = siteId =>
  selectFeatureAssociationsByEntityId(selectSiteFeatureAssociations, siteId);

// Select a single site feature association by site ID and feature key
export const selectSiteFeatureAssociationByKey = (siteId, key) =>
  selectFeatureAssociationByKeyAndId(selectSiteFeatureAssociations, siteId, key);
export const selectSiteGroupFeatureAssociationByKey = (siteGroupId, key) =>
  selectFeatureAssociationByKeyAndId(selectSiteGroupFeatureAssociations, siteGroupId, key);
export const selectCustomerFeatureAssociationByKey = (customerId, key) =>
  selectFeatureAssociationByKeyAndId(selectCustomerFeatureAssociations, customerId, key);

export const selectFeatureAssociationByEntityAndKey = (entityType, entityId, featureKey) => {
  switch (entityType) {
    case 'customer':
      return selectCustomerFeatureAssociationByKey(entityId, featureKey);
    case 'sitegroup':
      return selectSiteGroupFeatureAssociationByKey(entityId, featureKey);
    case 'site':
      return selectSiteFeatureAssociationByKey(entityId, featureKey);
    default:
      return () => null;
  }
};

// Adapter selectors for single feature association by ID
export const { selectById: selectCustomerFeatureById } = featureAssociationAdapter.getSelectors(
  selectCustomerFeatureAssociations,
);
export const { selectById: selectSiteGroupFeatureById } = featureAssociationAdapter.getSelectors(
  selectSiteGroupFeatureAssociations,
);
export const { selectById: selectSiteFeatureById } =
  featureAssociationAdapter.getSelectors(selectSiteFeatureAssociations);

const { reducer } = slice;
export default reducer;
